import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  BarController,
  BarElement,
  DoughnutController,
  ArcElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js"

Chart.register(
  LineController,
  LineElement,
  PointElement,
  BarController,
  BarElement,
  DoughnutController,
  ArcElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
)

const PALETTE = [
  "#1B628E",
  "#FF9670",
  "#DA8566",
  "#4B89AE",
  "#FFB89D",
  "#93B8CE",
  "#041927",
  "#2C7DB0",
  "#B85A3F",
  "#6B9FBF"
]

const PRIMARY = "#1B628E"
const PRIMARY_FILL = "rgba(27, 98, 142, 0.15)"

export default class extends Controller {
  static values = {
    type: String,
    points: Array,
    label: String,
    horizontal: Boolean,
    fill: Boolean,
    yLabel: String,
    series: Object,
    stacked: Boolean
  }

  connect() {
    let config

    if (this.hasSeriesValue && this.seriesValue.series) {
      config = this.buildStackedConfig(this.seriesValue.labels, this.seriesValue.series)
    } else {
      const labels = this.pointsValue.map(p => p.label)
      const data = this.pointsValue.map(p => p.value)
      config = this.buildConfig(labels, data)
    }

    this.chart = new Chart(this.element, config)
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }

  buildStackedConfig(labels, series) {
    const stacked = this.stackedValue
    const datasets = series.map((s, i) => ({
      label: s.label,
      data: s.data,
      backgroundColor: s.color || PALETTE[i % PALETTE.length],
      borderColor: s.color || PALETTE[i % PALETTE.length],
      borderWidth: 1
    }))

    return {
      type: this.typeValue === "line" ? "line" : "bar",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
        scales: {
          x: { stacked: stacked },
          y: {
            stacked: stacked,
            beginAtZero: true,
            title: { display: !!this.yLabelValue, text: this.yLabelValue }
          }
        }
      }
    }
  }

  buildConfig(labels, data) {
    const type = this.typeValue
    const isMulti = type === "doughnut" || type === "bar-multi"

    if (type === "doughnut") {
      return {
        type: "doughnut",
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: PALETTE.slice(0, data.length),
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } }
        }
      }
    }

    const isBar = type === "bar"
    const horizontal = this.horizontalValue

    return {
      type: isBar ? "bar" : "line",
      data: {
        labels,
        datasets: [{
          label: this.labelValue || "",
          data,
          backgroundColor: isMulti ? PALETTE.slice(0, data.length) : (this.fillValue ? PRIMARY_FILL : PRIMARY),
          borderColor: PRIMARY,
          borderWidth: 2,
          fill: this.fillValue,
          tension: 0.3,
          pointRadius: 3
        }]
      },
      options: {
        indexAxis: horizontal ? "y" : "x",
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: !!this.labelValue } },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: !!this.yLabelValue, text: this.yLabelValue }
          }
        }
      }
    }
  }
}
